import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Play, ChevronRight, ChevronDown, Plus, X, Loader2, RotateCw,
  KeyRound, FolderOpen, AlertTriangle, Square,
} from 'lucide-react'
import {
  startScriptRun, stopScriptRun, scriptRunStreamUrl, inferInterpreter, tokenizeArgs, getRunFromOptions,
  type ScriptRunArgs, type ScriptRunResult, type RunFromOption,
} from '../client/scriptRunner.js'
import { collapseProgressOutput } from '@papan/types/text'
import { useTranslation } from '@papan/i18n'
import { onSessionEnd } from '@papan/core-ui/ui/sessionEnded'
import { errorText } from '@papan/api-client'

interface ScriptRunPanelProps {
  fileId: string
  fileName?: string
  /** Script's own parent folder — the default "Run from:" root. */
  parentId?: string | null
}

interface EnvRow { id: number; key: string; value: string }

// Run-integrity reservations (NOT a security control — the sandbox is the
// boundary; this only stops a user clobbering the interpreter's own env).
const RESERVED_ENV = /^(LD_|PYTHON|PATH$|HOME$|PAPAN_|PWD$|SHELL$)/

let envRowSeq = 1

// Transient SSE drops auto-reconnect (EventSource default + full-backlog
// replay on the server). Give up only after this many consecutive fails
// so an expired/gone job can't retry forever.
const MAX_RECONNECTS = 8

export function ScriptRunPanel({ fileId, fileName, parentId }: ScriptRunPanelProps) {
  const inferred = useMemo(() => inferInterpreter(fileName), [fileName])
  // The drive root as Drive names it (#1119: a run-from folder like any other) — Drive's own string.
  const { t: tFiles } = useTranslation('files')
  const rootLabel = tFiles('driveView.rootFolder')

  const [optionsOpen, setOptionsOpen] = useState(false)
  const [runFromOpts, setRunFromOpts] = useState<RunFromOption[]>([])
  const [runFromId, setRunFromId] = useState<string | null>(parentId ?? null)
  const [rawArgs, setRawArgs] = useState('')
  const [timeoutSec, setTimeoutSec] = useState('')
  const [stdinOpen, setStdinOpen] = useState(false)
  const [stdin, setStdin] = useState('')
  const [envRows, setEnvRows] = useState<EnvRow[]>([])
  const [running, setRunning] = useState(false)
  const [stdout, setStdout] = useState('')
  const [stderr, setStderr] = useState('')
  const [result, setResult] = useState<ScriptRunResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Last config so "Run again" replays the exact scope+data.
  const lastArgs = useRef<ScriptRunArgs | null>(null)
  // Monotonic run token: only the most-recent run may write output.
  // Guards the "stale output appends / clobbers" symptom — a slow earlier
  // run resolving after a newer one must NOT touch the panel.
  const runToken = useRef(0)
  // Live SSE handle for the current run (closed on re-run / unmount).
  const esRef = useRef<EventSource | null>(null)
  // Current run's job id — the handle the explicit Stop targets. Closing the
  // SSE does NOT stop the run (it keeps going + saves outputs); only Stop does.
  const currentJobId = useRef<string | null>(null)
  const [stopping, setStopping] = useState(false)
  // Transient-drop bookkeeping for auto-reattach.
  const reconnects = useRef(0)
  const [reconnecting, setReconnecting] = useState(false)

  // Accessible ancestry for "Run from:" (server clips at the access
  // ceiling). On failure we keep the parent default — the worker still
  // re-enforces per-member permission + ancestry on run.
  useEffect(() => {
    let cancelled = false
    getRunFromOptions(fileId)
      .then(r => {
        if (cancelled) return
        setRunFromOpts(r.options)
        setRunFromId(r.defaultFolderId ?? parentId ?? null)
      })
      .catch(() => { /* keep parent default */ })
    return () => { cancelled = true }
  }, [fileId, parentId])

  const addEnvRow = useCallback(() => {
    setEnvRows(rows => [...rows, { id: envRowSeq++, key: '', value: '' }])
  }, [])
  const setEnvRow = useCallback((id: number, patch: Partial<EnvRow>) => {
    setEnvRows(rows => rows.map(r => (r.id === id ? { ...r, ...patch } : r)))
  }, [])
  const removeEnvRow = useCallback((id: number) => {
    setEnvRows(rows => rows.filter(r => r.id !== id))
  }, [])

  const buildArgs = useCallback((): ScriptRunArgs => {
    const env: Record<string, string> = {}
    for (const r of envRows) {
      const k = r.key.trim()
      if (k) env[k] = r.value
    }
    const t = parseInt(timeoutSec, 10)
    return {
      fileId,
      runFromFolderId: runFromId,
      args: rawArgs.trim() ? tokenizeArgs(rawArgs) : undefined,
      env: Object.keys(env).length ? env : undefined,
      stdin: stdin ? stdin : undefined,
      // Seconds; blank = the server default. The worker clamps to [1, 300].
      timeout: Number.isFinite(t) && t > 0 ? t : undefined,
    }
  }, [fileId, runFromId, rawArgs, envRows, stdin, timeoutSec])

  const doRun = useCallback(async (args: ScriptRunArgs) => {
    const token = ++runToken.current
    const stale = () => runToken.current !== token
    // Tear down any prior stream + reset BEFORE the run so output never
    // lingers or append-clobbers across runs.
    esRef.current?.close()
    esRef.current = null
    reconnects.current = 0
    setReconnecting(false)
    setStdout(''); setStderr(''); setResult(null); setError(null); setRunning(true)
    setStopping(false); currentJobId.current = null
    lastArgs.current = args
    let done = false
    try {
      const { jobId } = await startScriptRun(args)
      if (stale()) return
      currentJobId.current = jobId
      const es = new EventSource(scriptRunStreamUrl(jobId), { withCredentials: true })
      esRef.current = es

      // #874: a result too large for one frame arrives as `done-part` frames
      // ({ s, d }) before a `done` marked `parts: true`; assembled here.
      let doneOut = '', doneErr = ''
      es.addEventListener('open', () => {
        doneOut = ''; doneErr = ''
        // The server replays the FULL backlog on every (re)connect — reset
        // so a reconnect rebuilds identical output instead of doubling it.
        if (stale()) return
        reconnects.current = 0
        setReconnecting(false)
        setError(null)
        setStdout(''); setStderr('')
      })
      es.addEventListener('chunk', (ev: MessageEvent) => {
        if (stale()) { es.close(); return }
        try {
          const { s, d } = JSON.parse(ev.data) as { s: 'o' | 'e'; d: string }
          if (s === 'e') setStderr(p => p + d)
          else setStdout(p => p + d)
        } catch { /* ignore a malformed frame */ }
      })
      es.addEventListener('done-part', (ev: MessageEvent) => {
        if (stale()) { es.close(); return }
        try {
          const { s, d } = JSON.parse(ev.data) as { s: 'o' | 'e'; d: string }
          if (s === 'e') doneErr += d
          else doneOut += d
        } catch { /* ignore a malformed frame */ }
      })
      es.addEventListener('done', (ev: MessageEvent) => {
        done = true
        es.close()
        if (stale()) return
        try {
          const parsed = JSON.parse(ev.data) as ScriptRunResult
          const r = parsed.parts ? { ...parsed, stdout: doneOut, stderr: doneErr } : parsed
          setResult(r)
          // Snap to the authoritative final output (covers a live cap/miss).
          setStdout(r.stdout); setStderr(r.stderr)
        } catch { setError('Malformed result from the run stream') }
        setRunning(false)
      })
      // EventSource delivers BOTH a server-sent `event: error` (has
      // .data — terminal) and native transport errors (no .data —
      // transient: let EventSource auto-reconnect; the server replays the
      // full backlog from Redis on the new connection, so 'open' rebuilds
      // identical output and the run continues seamlessly).
      es.addEventListener('error', (ev: MessageEvent) => {
        if (done || stale()) return
        if (ev?.data) {
          es.close()
          try { setError((JSON.parse(ev.data) as { error?: string }).error || 'Run stream error') }
          catch { setError('Run stream error') }
          setRunning(false)
          setReconnecting(false)
          return
        }
        reconnects.current += 1
        if (reconnects.current > MAX_RECONNECTS) {
          es.close()
          setError('Live stream lost after several reconnect attempts — Run again to reattach.')
          setRunning(false)
          setReconnecting(false)
          return
        }
        // Do NOT close — EventSource retries; the job keeps running and
        // its output is held in Redis (10 min). Keep `running` true.
        setReconnecting(true)
      })
    } catch (e) {
      if (stale()) return
      // #1316: a start dropped at a session end shows no error (the listener below has already ended the run)
      const text = errorText(e)
      if (text !== null) setError(text)
      setRunning(false)
    }
  }, [])

  // Close the live stream when the panel unmounts (tab close / navigate).
  useEffect(() => () => { esRef.current?.close(); esRef.current = null }, [])
  // #1316: the stream belongs to the session — ending it closes the stream (EventSource would otherwise keep delivering
  // the old session's output, and its own auto-reconnect would re-open it as the next one). The run is abandoned.
  // A run whose START is still out ends too (review round 1, C-M5 / D-M3): its answer is dropped, and nothing of it is
  // shown — not an error, not a stream opened for the next session.
  useEffect(() => onSessionEnd(() => {
    runToken.current++
    esRef.current?.close()
    esRef.current = null
    setRunning(false)
    setReconnecting(false)
  }), [])

  const onRun = useCallback(() => { void doRun(buildArgs()) }, [doRun, buildArgs])
  const onRunAgain = useCallback(() => {
    if (lastArgs.current) void doRun(lastArgs.current)
  }, [doRun])
  // Explicit Stop — kill the running script + free its sandbox slot. The worker
  // kill makes the run emit a terminal `done` (non-zero exit), which flips the
  // panel back to idle through the normal stream path; `stopping` just guards
  // the button in the meantime.
  const onStop = useCallback(async () => {
    const jobId = currentJobId.current
    if (!jobId) return
    setStopping(true)
    try { await stopScriptRun(jobId) }
    catch { setStopping(false) /* let the user retry; the run may still be live */ }
  }, [])

  if (!inferred) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 themed-text-muted p-6">
        <AlertTriangle size={20} className="text-yellow-500" />
        <p className="text-sm">This file type isn’t runnable. Supported: <code>.py</code>, <code>.sh</code>.</p>
      </div>
    )
  }

  const reservedHit = envRows.some(r => r.key.trim() && RESERVED_ENV.test(r.key.trim()))

  return (
    <div className="flex-1 flex flex-col overflow-hidden themed-bg-primary">
      {/* Action row */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b themed-border shrink-0 flex-wrap">
        <button
          onClick={onRun}
          disabled={running}
          className="themed-btn-primary flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          Run (<span className="font-mono">{inferred.interp}</span>)
        </button>
        {running && (
          <button
            onClick={() => void onStop()}
            disabled={stopping}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-40"
            title="Stop the running script and free its sandbox slot"
          >
            {stopping ? <Loader2 size={13} className="animate-spin" /> : <Square size={13} />}
            Stop
          </button>
        )}
        <button
          onClick={onRunAgain}
          disabled={running || !lastArgs.current}
          className="themed-btn-secondary flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm disabled:opacity-40"
          title="Re-run with the last configuration"
        >
          <RotateCw size={13} />
          Run again
        </button>
        <button
          onClick={() => setOptionsOpen(o => !o)}
          className="flex items-center gap-1 px-2 py-1.5 rounded text-sm themed-text-secondary hover:themed-text"
        >
          {optionsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Options
        </button>
        <div className="flex-1" />
        <span className="text-xs themed-text-muted truncate max-w-[40%]" title={fileName}>{fileName}</span>
      </div>

      {/* Options */}
      {optionsOpen && (
        <div className="px-4 py-3 border-b themed-border shrink-0 space-y-3 text-sm">
          <div>
            <label className="block text-xs themed-text-muted mb-1">Run from</label>
            <div className="flex items-center gap-2">
              <FolderOpen size={14} className="themed-icon shrink-0" />
              <select
                className="themed-input rounded px-2 py-1 text-sm flex-1"
                value={runFromId ?? ''}
                onChange={e => setRunFromId(e.target.value || null)}
                disabled={runFromOpts.length === 0}
              >
                {runFromOpts.length === 0 ? (
                  <option value="">This script’s folder</option>
                ) : (
                  runFromOpts.map((o, i) => (
                    <option key={o.folderId} value={o.folderId}>
                      {o.isDriveRoot ? rootLabel : o.name}{i === 0 ? ' — this script’s folder' : ` — /${o.relPath}`}
                    </option>
                  ))
                )}
              </select>
            </div>
            <p className="text-[11px] themed-text-muted mt-1">
              Reads &amp; writes inside{' '}
              <span className="font-mono">
                /{runFromOpts.find(o => o.folderId === runFromId)?.relPath ?? '…'}
              </span>.
            </p>
          </div>

          <div>
            <label className="block text-xs themed-text-muted mb-1">Arguments</label>
            <input
              value={rawArgs}
              onChange={e => setRawArgs(e.target.value)}
              placeholder="--input data.csv --limit 100"
              spellCheck={false}
              className="themed-input rounded px-2 py-1 text-sm w-full font-mono"
            />
            <button
              onClick={() => setStdinOpen(s => !s)}
              className="flex items-center gap-1 mt-1.5 text-[11px] themed-text-muted hover:themed-text"
            >
              {stdinOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              stdin
            </button>
            {stdinOpen && (
              <textarea
                value={stdin}
                onChange={e => setStdin(e.target.value)}
                rows={3}
                spellCheck={false}
                placeholder="Piped to the process on stdin"
                className="themed-input rounded px-2 py-1 text-sm w-full font-mono mt-1 resize-y"
              />
            )}
          </div>

          <div>
            <label className="block text-xs themed-text-muted mb-1">Timeout</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={300}
                value={timeoutSec}
                onChange={e => setTimeoutSec(e.target.value)}
                placeholder="60"
                className="themed-input rounded px-2 py-1 text-sm w-24 font-mono"
              />
              <span className="text-[11px] themed-text-muted">seconds — blank = 60s default, max 300s</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs themed-text-muted">Environment</label>
              <button
                onClick={addEnvRow}
                className="flex items-center gap-1 text-[11px] themed-text-secondary hover:themed-text"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            {envRows.length === 0 && (
              <p className="text-[11px] themed-text-muted">Clean base env — no agent or tenant credentials inherited.</p>
            )}
            {envRows.map(r => (
              <div key={r.id} className="flex items-center gap-1.5 mb-1">
                <input
                  value={r.key}
                  onChange={e => setEnvRow(r.id, { key: e.target.value })}
                  placeholder="KEY"
                  spellCheck={false}
                  className="themed-input rounded px-2 py-1 text-xs font-mono w-1/3"
                />
                <span className="themed-text-muted">=</span>
                <input
                  value={r.value}
                  onChange={e => setEnvRow(r.id, { value: e.target.value })}
                  placeholder="value"
                  spellCheck={false}
                  className="themed-input rounded px-2 py-1 text-xs font-mono flex-1"
                />
                <button onClick={() => removeEnvRow(r.id)} className="themed-text-muted hover:text-red-400 p-1">
                  <X size={13} />
                </button>
              </div>
            ))}
            {reservedHit && (
              <p className="text-[11px] text-yellow-500 mt-1 flex items-center gap-1">
                <AlertTriangle size={11} /> A reserved name (PATH/HOME/PYTHON*/LD_*…) may break the interpreter.
              </p>
            )}
            <button
              disabled
              title="Keybook binding arrives in a later increment"
              className="flex items-center gap-1 mt-1.5 text-[11px] themed-text-muted opacity-50 cursor-not-allowed"
            >
              <KeyRound size={12} /> From Keybook…
            </button>
          </div>
        </div>
      )}

      {/* Output — live stream while running, authoritative on done */}
      <div className="flex-1 overflow-auto p-4 font-mono text-xs">
        {!result && !running && !error && !stdout && !stderr && (
          <p className="themed-text-muted">Press Run to execute this script in the sandbox.</p>
        )}

        {result ? (
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                result.timedOut
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : result.exitCode === 0 && !result.error
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
              }`}
            >
              {result.timedOut
                ? `Timed out at ${(result.durationMs / 1000).toFixed(0)}s`
                : `exit ${result.exitCode} · ${(result.durationMs / 1000).toFixed(1)}s`}
            </span>
            {/* D95 revision — the run FAILED: some files could not be checked, so they were not saved */}
            {result.error === 'files_not_saved' && (
              <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-red-500/20 text-red-400">
                {`${result.unsavedTotal ?? result.unsaved?.length ?? 0} file(s) not saved`}
              </span>
            )}
            <span className="themed-text-muted">{result.interpreter}</span>
          </div>
        ) : running ? (
          <p className={`flex items-center gap-2 mb-2 ${reconnecting ? 'text-yellow-500' : 'themed-text-muted'}`}>
            <Loader2 size={13} className="animate-spin" />
            {reconnecting ? 'Reconnecting to the running script…' : 'Running…'}
          </p>
        ) : null}

        {stdout && (
          <pre className="whitespace-pre-wrap break-words themed-text">{collapseProgressOutput(stdout)}</pre>
        )}
        {stderr && (
          <pre className="whitespace-pre-wrap break-words text-red-400 mt-2">{collapseProgressOutput(stderr)}</pre>
        )}

        {error && (
          <div className="text-red-400 flex items-start gap-2 mt-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span className="whitespace-pre-wrap break-words">{error}</span>
          </div>
        )}

        {result && result.unsaved && result.unsaved.length > 0 && (
          <div className="mt-3 pt-3 border-t themed-border">
            <p className="text-red-400 mb-1">Not saved to the drive — access couldn’t be checked</p>
            {result.unsaved.map((f, i) => (
              <div key={`${f.path ?? ''}:${i}`} className="flex items-center gap-2">
                <span className="text-red-400">!</span>
                <span className="themed-text">{f.path ?? f.stage}</span>
                <span className="themed-text-muted">{f.disposition === 'put-back' ? 'put back as it is in the drive' : f.disposition === 'still-in-drive' ? 'not deleted — still in the drive' : f.disposition}</span>
              </div>
            ))}
          </div>
        )}

        {result && result.filesChanged.length > 0 && (
          <div className="mt-3 pt-3 border-t themed-border">
            <p className="themed-text-muted mb-1">
              Files changed{result.filesChangedTracked ? '' : ' (not tracked — folder too large)'}
            </p>
            {result.filesChanged.map(f => (
              <div key={f.path} className="flex items-center gap-2">
                <span className={f.change === 'added' ? 'text-green-400' : 'text-yellow-400'}>
                  {f.change === 'added' ? '+' : '~'}
                </span>
                <span className="themed-text">{f.path}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
